import { Dispatch, SetStateAction, useCallback, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, DialogContent, Grid, Stack, Typography } from "@mui/material"
import { TroubleshootingCards, TroubleshootingTypeContent } from "./components/TroubleshootingCards"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { Troubleshooting } from "./components/Troubleshooting"
import { SupportContent } from "./components/SupportContent"
import { TrainingGuides } from "./components/TrainingGuides"
import { StandardDialog } from "../StandardDialog"
import { selectEffectiveUserType } from "../../../../modules/context/contextSelectors"
import { useAppSelector } from "../../../../modules/store/storeHooks"
import { UserType } from "../../../../modules/login/loginTypes"

const useStyles = makeStyles()((theme) => ({
    dialogPaper: {
        background: theme.palette.tailwind.slate[100],
    },
    dialogContent: {
        padding: "0.6rem 3.75rem 3.75rem 3.75rem",
        display: "flex",
    },
    dialogBox: {
        gap: "2.5rem",
    },
    title: {
        color: theme.palette.tailwind.slate[900],
    },

    transitionBox: {
        transition: "opacity 0.5s",
        opacity: 1,
    },
}))

type HelpDialogProps = Readonly<{
    dialogState: boolean
    setDialogState: Dispatch<SetStateAction<boolean>>
}>

export const HelpDialog = ({ dialogState, setDialogState }: HelpDialogProps) => {
    const { classes } = useStyles()
    const userType = useAppSelector(selectEffectiveUserType)

    const [showSupport, setShowSupport] = useState<{ show: boolean; type: TroubleshootingTypeContent }>({
        show: true,
        type: null,
    })

    const handleClose = useCallback(() => {
        setDialogState((prev) => !prev)
    }, [setDialogState])

    const handleToggleSupport = (type: TroubleshootingTypeContent) => {
        const transitionBox = document.getElementById("transitionBox")
        if (transitionBox) {
            transitionBox.style.opacity = "0"

            setTimeout(() => {
                setShowSupport((prev) => {
                    return { show: !prev.show, type }
                })
                transitionBox.style.opacity = "1"
            }, 500) // Match the duration of the opacity transition
        }
    }

    return (
        <StandardDialog
            dialogTitle={<></>}
            open={dialogState}
            onClose={handleClose}
            maxWidthProp="lg"
            scroll="body"
            slotProps={{ paper: { className: classes.dialogPaper } }}
        >
            <DialogContent className={classes.dialogContent}>
                <Box id="transitionBox" className={classes.transitionBox}>
                    {showSupport.show ? (
                        <Stack gap="3.75rem">
                            <Stack gap="2rem">
                                <LocalizedStrict id="help-dialog-search-title">
                                    <Typography variant="h3" className={classes.title}>
                                        Hello, how can we help you?
                                    </Typography>
                                </LocalizedStrict>
                            </Stack>
                            <Grid container flexDirection="column" className={classes.dialogBox}>
                                {userType === UserType.MERCHANT && (
                                    <Troubleshooting handleCardClick={handleToggleSupport} />
                                )}
                                <TrainingGuides />
                                <SupportContent />
                            </Grid>
                        </Stack>
                    ) : (
                        <TroubleshootingCards type={showSupport.type} handleBack={handleToggleSupport} />
                    )}
                </Box>
            </DialogContent>
        </StandardDialog>
    )
}
